import { Person, Provider, PersonDocument, IdentityDocument } from '../types/provider'

/**
 * Finds the best matching document from the documents array.
 * Prioritizes documents with storageKey and returns the most recent one (last in array).
 */
const findBestDocument = (
  documents: PersonDocument[],
  kind: string,
  side: string,
): PersonDocument | undefined => {
  // Filter documents matching kind and side
  const matchingDocs = documents.filter(doc => doc.kind === kind && doc.side === side)

  if (matchingDocs.length === 0) {
    return undefined
  }

  // Prefer documents with storageKey, use the last one (most recent)
  const docsWithStorageKey = matchingDocs.filter(doc => doc.storageKey)
  if (docsWithStorageKey.length > 0) {
    return docsWithStorageKey[docsWithStorageKey.length - 1]
  }

  // Fall back to last matching document even without storageKey
  return matchingDocs[matchingDocs.length - 1]
}

/**
 * Maps a person's documents array to the verification.document structure.
 * The API returns documents in a `documents` array with full document details,
 * but the frontend expects document storage keys in verification.document.front/back.
 *
 * Also handles flat identity document fields (identityDocType, identityDocFront, etc.)
 * that may be returned directly on the person object.
 *
 * @param person - Person object from API response
 * @returns Person with documents mapped to verification.document.front/back
 */
export const mapPersonDocuments = (person: Person): Person => {
  const currentVerification = person.verification || {}
  const currentDocument = currentVerification.document

  // First, check for flat identity document fields on the person object
  const flatFront = person.identityDocFront?.storageKey
  const flatBack = person.identityDocBack?.storageKey
  const flatType = person.identityDocType
  const flatNumber = person.identityDocNumber
  const flatCountry = person.identityDocCountry
  const flatExpiry = person.identityDocExpiry

  // Then check the documents array
  let arrayFront: string | undefined
  let arrayBack: string | undefined
  let arrayType: IdentityDocument['type'] | undefined
  let arrayNumber: string | undefined
  let arrayCountry: string | undefined
  let arrayExpiry: string | undefined

  if (person.documents && person.documents.length > 0) {
    const frontDoc = findBestDocument(person.documents, 'id_document', 'front')
    const backDoc = findBestDocument(person.documents, 'id_document', 'back')

    arrayFront = frontDoc?.storageKey
    arrayBack = backDoc?.storageKey
    arrayType = (frontDoc?.type || backDoc?.type) as IdentityDocument['type'] | undefined
    arrayNumber = frontDoc?.number || backDoc?.number
    arrayCountry = frontDoc?.country || backDoc?.country
    arrayExpiry = frontDoc?.expiryDate || backDoc?.expiryDate
  }

  // Merge all sources: current document > flat fields > documents array
  const front = currentDocument?.front || flatFront || arrayFront
  const back = currentDocument?.back || flatBack || arrayBack
  const type = currentDocument?.type || flatType || arrayType
  const number = currentDocument?.number || flatNumber || arrayNumber
  const country = currentDocument?.country || flatCountry || arrayCountry
  const expiryDate = currentDocument?.expiryDate || flatExpiry || arrayExpiry

  // Only update verification if we actually have document data
  if (front || back || type || number) {
    const updatedDocument: Partial<IdentityDocument> = {
      ...currentDocument,
      front,
      back,
      type,
      number,
      country,
      expiryDate,
    }

    return {
      ...person,
      verification: {
        ...currentVerification,
        document: updatedDocument as IdentityDocument,
      },
    }
  }

  return person
}

/**
 * Maps all people's documents in a provider to the verification.document structure.
 *
 * @param provider - Provider object from API response
 * @returns Provider with all people's documents mapped
 */
export const mapProviderPeopleDocuments = (provider: Provider): Provider => {
  if (!provider.legalEntity?.people || provider.legalEntity.people.length === 0) {
    return provider
  }

  const mappedPeople = provider.legalEntity.people.map(mapPersonDocuments)

  return {
    ...provider,
    legalEntity: {
      ...provider.legalEntity,
      people: mappedPeople,
    },
  }
}
